import { ArrowUpRight, Check, ShoppingBag, Radar, Activity, Mail } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import TempMailDemo from "@/components/TempMailDemo";

const projectArt = {
    tempmail: { icon: Mail, grad: "from-neon/20 via-ink2 to-ink2" },
    commerce: { icon: ShoppingBag, grad: "from-cobalt/25 via-ink2 to-ink2" },
    fleetops: { icon: Radar, grad: "from-mint/20 via-ink2 to-ink2" },
    medisync: { icon: Activity, grad: "from-neon/15 via-ink2 to-ink2" },
};

export default function Projects() {
    const { t } = useLanguage();
    const p = t.projectsPage;
    const [featured, ...rest] = t.projects.items;

    return (
        <div data-testid="projects-page" className="pt-36 sm:pt-44">
            <section className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <div className="grid-bg absolute inset-0 -z-10" aria-hidden="true" />
                <Reveal>
                    <Eyebrow>{p.eyebrow}</Eyebrow>
                    <h1 className="max-w-3xl font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
                        {p.title}
                    </h1>
                    <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{p.sub}</p>
                </Reveal>
            </section>

            {/* FEATURED: TEMP MAIL */}
            <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
                <Reveal>
                    <div
                        className="glass relative overflow-hidden rounded-3xl p-8 sm:p-12"
                        data-testid="project-card-tempmail"
                    >
                        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-neon/10 blur-[100px]" aria-hidden="true" />
                        <div className="relative grid items-center gap-12 lg:grid-cols-2">
                            <div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                                        {featured.name}
                                    </h2>
                                    <span className="flex items-center gap-1.5 rounded-full border border-mint/30 bg-mint/10 px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-widest text-mint">
                                        <span className="status-dot h-1.5 w-1.5 rounded-full bg-mint" />
                                        {featured.status}
                                    </span>
                                </div>
                                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                                    {featured.category}
                                </p>
                                <p className="mt-6 text-sm leading-relaxed text-slate-400 sm:text-base">
                                    {featured.desc}
                                </p>

                                <h3 className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-neon">
                                    {p.highlightsLabel}
                                </h3>
                                <ul className="mt-4 space-y-2.5">
                                    {featured.highlights.map((hl, i) => (
                                        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                                            {hl}
                                        </li>
                                    ))}
                                </ul>

                                <h3 className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-neon">
                                    {p.techLabel}
                                </h3>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {featured.tech.map((tech) => (
                                        <span
                                            key={tech}
                                            className="rounded-full border border-slate-800 bg-ink px-3 py-1.5 font-mono text-[10px] text-slate-400"
                                        >
                                            {tech}
                                        </span>
                                    ))}
                                </div>

                                <a
                                    href="https://maxthsolutions.org"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="project-tempmail-visit"
                                    className="group mt-9 inline-flex items-center gap-2 rounded-full bg-neon px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-white"
                                >
                                    {p.visit}
                                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </a>
                            </div>
                            <TempMailDemo />
                        </div>
                    </div>
                </Reveal>

                {/* OTHER PROJECTS */}
                <div className="mt-8 grid gap-5 lg:grid-cols-3">
                    {rest.map((proj, i) => {
                        const art = projectArt[proj.id];
                        const Icon = art.icon;
                        return (
                            <Reveal key={proj.id} delay={0.08 * i} className={i === 1 ? "lg:translate-y-8" : ""}>
                                <div
                                    className="glass card-lift flex h-full flex-col overflow-hidden rounded-2xl"
                                    data-testid={`project-card-${proj.id}`}
                                >
                                    <div className={`grid-bg relative flex h-44 items-center justify-center bg-gradient-to-br ${art.grad}`}>
                                        <Icon className="h-12 w-12 text-slate-500" />
                                        <span className="absolute bottom-3 left-4 rounded-full bg-ink/80 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400 backdrop-blur">
                                            {proj.status}
                                        </span>
                                    </div>
                                    <div className="flex flex-1 flex-col p-7">
                                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                            {proj.category}
                                        </p>
                                        <h3 className="mt-2 font-display text-xl font-bold text-white">{proj.name}</h3>
                                        <p className="mt-3 text-sm leading-relaxed text-slate-400">{proj.desc}</p>

                                        <h4 className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-neon">
                                            {p.highlightsLabel}
                                        </h4>
                                        <ul className="mt-3 flex-1 space-y-2">
                                            {proj.highlights.map((hl, j) => (
                                                <li key={j} className="flex items-start gap-2 text-xs leading-relaxed text-slate-400">
                                                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-neon" />
                                                    {hl}
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="mt-6 flex flex-wrap gap-2">
                                            {proj.tech.map((tech) => (
                                                <span
                                                    key={tech}
                                                    className="rounded-full border border-slate-800 bg-ink px-2.5 py-1 font-mono text-[10px] text-slate-500"
                                                >
                                                    {tech}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
